/**
 * assets/otp-login.js  (Shopify theme asset)
 *
 * OTP login widget — phone → OTP → (new customer details) → Multipass redirect
 *
 * window.__olConfig is injected by otp-login-widget.liquid:
 *   {
 *     apiBase:  string,  — App Proxy URL e.g. "/apps/shopify-otp"
 *     termsUrl: string,  — T&C page URL
 *     privacyUrl: string — Privacy policy URL
 *   }
 */

(function () {
  'use strict';

  const cfg        = window.__olConfig || {};
  const rawPath    = (cfg.proxyPath || '/apps/otp').replace(/\/+$/, '');
  const proxyBase  = rawPath.startsWith('/') ? rawPath : `/${rawPath}`;
  const PROXY_PATH = `${window.location.origin}${proxyBase}`;

  let fullPhone    = '';
  let sessionToken = '';
  let isNewCust    = false;
  let timerHandle  = null;
  const RESEND_SEC = 30;

  /* ── DOM helpers ─────────────────────────────────────────────────────── */
  const $  = id  => document.getElementById(id);
  const $$ = sel => Array.from(document.querySelectorAll(sel));

  const steps = {
    phone:   $('ol-step-phone'),
    otp:     $('ol-step-otp'),
    details: $('ol-step-details'),
    success: $('ol-step-success'),
  };

  function showStep(name) {
    Object.values(steps).forEach(el => el && el.classList.add('ol-step--hidden'));
    if (steps[name]) steps[name].classList.remove('ol-step--hidden');
  }

  function setError(elId, msg) {
    const el = $(elId);
    if (el) el.textContent = msg;
  }

  function clearError(elId) { setError(elId, ''); }

  /* ── STEP 1 — Phone + T&C ───────────────────────────────────────────── */
  const phoneInput = $('ol-phone');
  const tcCheckbox = $('ol-tc-checkbox');
  const sendBtn    = $('ol-send-btn');

  function updateSendBtn() {
    if (sendBtn) {
      sendBtn.disabled = !(phoneInput?.value.trim().length === 10 && tcCheckbox?.checked);
    }
  }

  phoneInput?.addEventListener('input', () => {
    phoneInput.value = phoneInput.value.replace(/\D/g, '').slice(0, 10);
    clearError('ol-phone-error');
    updateSendBtn();
  });

  tcCheckbox?.addEventListener('change', updateSendBtn);

  sendBtn?.addEventListener('click', async () => {
    clearError('ol-phone-error');
    const num = phoneInput?.value.trim();
    if (!num || num.length !== 10) {
      setError('ol-phone-error', 'Please enter a valid 10-digit mobile number.');
      return;
    }
    if (!tcCheckbox?.checked) {
      setError('ol-phone-error', 'Please accept the Terms & Conditions to continue.');
      return;
    }

    fullPhone = '+91' + num;
    sendBtn.disabled    = true;
    sendBtn.textContent = 'Sending…';

    try {
      const res  = await fetch(`${PROXY_PATH}/login/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: fullPhone }),
      });
      const data = await res.json();

      if (!data.success) {
        setError('ol-phone-error', data.message || 'Failed to send OTP. Please try again.');
        sendBtn.disabled    = false;
        sendBtn.textContent = 'Send OTP';
        return;
      }

      $('ol-phone-display').textContent = fullPhone;
      clearOTPBoxes();
      showStep('otp');
      $$('.ol-otp-box')[0]?.focus();
      startTimer();
    } catch {
      setError('ol-phone-error', 'Network error. Please try again.');
      sendBtn.disabled    = false;
      sendBtn.textContent = 'Send OTP';
    }
  });

  /* ── STEP 2 — OTP ───────────────────────────────────────────────────── */
  const otpBoxes  = $$('.ol-otp-box');
  const verifyBtn = $('ol-verify-btn');
  const changeBtn = $('ol-change-phone');
  const resendBtn = $('ol-resend-btn');
  const timerEl   = $('ol-timer');
  const timerSecs = $('ol-timer-secs');

  function clearOTPBoxes() {
    otpBoxes.forEach(b => { b.value = ''; b.classList.remove('ol-filled'); });
    if (verifyBtn) verifyBtn.disabled = true;
    clearError('ol-otp-error');
  }

  function getCode() { return otpBoxes.map(b => b.value).join(''); }

  function checkOTPComplete() {
    if (verifyBtn) verifyBtn.disabled = !otpBoxes.every(b => b.value);
  }

  otpBoxes.forEach((box, i) => {
    box.addEventListener('input', () => {
      box.value = box.value.replace(/\D/g, '').slice(-1);
      box.classList.toggle('ol-filled', !!box.value);
      if (box.value && i < otpBoxes.length - 1) otpBoxes[i + 1].focus();
      checkOTPComplete();
      clearError('ol-otp-error');
    });

    box.addEventListener('keydown', e => {
      if (e.key === 'Backspace' && !box.value && i > 0) {
        otpBoxes[i - 1].value = '';
        otpBoxes[i - 1].classList.remove('ol-filled');
        otpBoxes[i - 1].focus();
        checkOTPComplete();
      }
    });

    box.addEventListener('paste', e => {
      e.preventDefault();
      const digits = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
      digits.split('').forEach((ch, j) => {
        if (otpBoxes[j]) { otpBoxes[j].value = ch; otpBoxes[j].classList.add('ol-filled'); }
      });
      checkOTPComplete();
    });
  });

  verifyBtn?.addEventListener('click', async () => {
    clearError('ol-otp-error');
    const code = getCode();
    verifyBtn.disabled    = true;
    verifyBtn.textContent = 'Verifying…';

    try {
      const res  = await fetch(`${PROXY_PATH}/login/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: fullPhone, otp: code }),
      });
      const data = await res.json();

      if (!data.success) {
        setError('ol-otp-error', data.message || 'Incorrect code. Please try again.');
        verifyBtn.disabled    = false;
        verifyBtn.textContent = 'Verify';
        return;
      }

      clearInterval(timerHandle);
      sessionToken = data.sessionToken;
      isNewCust    = data.isNewCustomer;

      if (isNewCust) {
        showStep('details');
        $('ol-details-phone').textContent = fullPhone;
        $('ol-name-input')?.focus();
      } else {
        await completeLogin();
      }
    } catch {
      setError('ol-otp-error', 'Network error. Please try again.');
      verifyBtn.disabled    = false;
      verifyBtn.textContent = 'Verify';
    }
  });

  changeBtn?.addEventListener('click', () => {
    clearInterval(timerHandle);
    if (sendBtn) { sendBtn.disabled = false; sendBtn.textContent = 'Send OTP'; }
    showStep('phone');
  });

  resendBtn?.addEventListener('click', () => {
    resendBtn.style.display = 'none';
    if (timerEl) timerEl.style.display = '';
    clearOTPBoxes();
    otpBoxes[0]?.focus();

    fetch(`${PROXY_PATH}/login/send-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone: fullPhone }),
    }).catch(() => {});

    startTimer();
  });

  /* ── STEP 3 — New customer details ──────────────────────────────────── */
  const nameInput  = $('ol-name-input');
  const emailInput = $('ol-email-input');
  const detailsBtn = $('ol-details-btn');

  detailsBtn?.addEventListener('click', async () => {
    clearError('ol-details-error');
    const name  = nameInput?.value.trim();
    const email = emailInput?.value.trim();

    if (!name) {
      setError('ol-details-error', 'Please enter your full name.');
      return;
    }
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('ol-details-error', 'Please enter a valid email address.');
      return;
    }

    detailsBtn.disabled    = true;
    detailsBtn.textContent = 'Creating account…';

    await completeLogin({ name, email });
  });

  /* ── Complete login (both new and existing customers) ───────────────── */
  async function completeLogin(extra = {}) {
    const completeBtn = isNewCust ? $('ol-details-btn') : verifyBtn;

    try {
      const res  = await fetch(`${PROXY_PATH}/login/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: sessionToken,
          ...extra,
          returnTo: cfg.returnTo || undefined,
        }),
      });
      const data = await res.json();

      if (!data.success) {
        const errEl = isNewCust ? 'ol-details-error' : 'ol-otp-error';
        setError(errEl, data.message || 'Login failed. Please try again.');
        if (completeBtn) { completeBtn.disabled = false; completeBtn.textContent = isNewCust ? 'Continue' : 'Verify'; }
        return;
      }

      showStep('success');
      // Small delay so the user sees the success state before redirect
      setTimeout(() => { window.location.href = data.redirectUrl; }, 800);
    } catch {
      const errEl = isNewCust ? 'ol-details-error' : 'ol-otp-error';
      setError(errEl, 'Network error. Please try again.');
      if (completeBtn) { completeBtn.disabled = false; completeBtn.textContent = isNewCust ? 'Continue' : 'Verify'; }
    }
  }

  /* ── Timer ──────────────────────────────────────────────────────────── */
  function startTimer() {
    let secs = RESEND_SEC;
    if (timerSecs) timerSecs.textContent = secs;
    if (timerEl)   timerEl.style.display = '';
    if (resendBtn) resendBtn.style.display = 'none';
    clearInterval(timerHandle);

    timerHandle = setInterval(() => {
      secs--;
      if (timerSecs) timerSecs.textContent = secs;
      if (secs <= 0) {
        clearInterval(timerHandle);
        if (timerEl)   timerEl.style.display = 'none';
        if (resendBtn) resendBtn.style.display = 'inline';
      }
    }, 1000);
  }

  /* ── Init ───────────────────────────────────────────────────────────── */
  showStep('phone');
  updateSendBtn();
})();
